<script setup lang="ts">
import { ref, useTemplateRef } from 'vue';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import CheckMarkIconRound from '@/assets/icons/CheckMarkIconRound.vue';

const props = defineProps<{
  showTeamInvite: boolean;
  isSimpleSuccess?: boolean;
  onInviteTeam?: () => Promise<void> | void;
  onPrimary?: () => Promise<void> | void;
  onSecondary?: () => Promise<void> | void;
  onDismiss?: () => Promise<void> | void;
  primaryLabel?: string;
  secondaryLabel?: string;
  hintText?: string;
  onClose?: () => void;
}>();

const dialogRef = useTemplateRef('dialogRef');
const isLoading = ref(false);
const actionHandled = ref(false);

const show = async (): Promise<void> => {
  actionHandled.value = false;
  await dialogRef.value?.show();
};

const hide = (): void => {
  dialogRef.value?.hide();
};

const handleHide = async (): Promise<void> => {
  if (!actionHandled.value) {
    actionHandled.value = true;
    if (props.onDismiss) {
      isLoading.value = true;
      await props.onDismiss();
      isLoading.value = false;
    }
  }
  props.onClose?.();
};

const handleInvite = async (): Promise<void> => {
  actionHandled.value = true;
  if (props.onInviteTeam) {
    isLoading.value = true;
    await props.onInviteTeam();
    isLoading.value = false;
  }
  hide();
};

const handlePrimary = async (): Promise<void> => {
  actionHandled.value = true;
  if (props.onPrimary) {
    isLoading.value = true;
    await props.onPrimary();
    isLoading.value = false;
  }
  hide();
};

const handleSecondary = async (): Promise<void> => {
  actionHandled.value = true;
  if (props.onSecondary) {
    isLoading.value = true;
    await props.onSecondary();
    isLoading.value = false;
  }
  hide();
};

defineExpose({
  show,
});
</script>

<template>
  <DialogBase
    ref="dialogRef"
    :show-header="false"
    disable-internal-hide-logic
    enter-animation="slide-up"
    no-padding
    content-width-style="w-auto max-w-[480px]"
    mobile-presentation="bottom-sheet"
    class="w-min-0"
    @hide="handleHide"
  >
    <div
      class="mx-auto flex w-full max-w-[320px] flex-col items-center gap-xl px-lg pb-mobile-safe-bottom-lg pt-lg text-center md:max-w-none md:p-[80px]"
    >
      <CheckMarkIconRound
        :theme="'primary'"
        :class="props.isSimpleSuccess ? '!size-[72px] !bg-success' : '!size-[80px] !bg-success'"
      />
      <h2 class="text-title font-medium text-primary">{{ $t('growth.firstEntrySuccess.title') }}</h2>
      <template v-if="props.isSimpleSuccess">
        <CustomButton class="justify-center w-full" theme="primary" :disabled="isLoading" @click="hide">
          {{ $t('growth.firstEntrySuccess.doneCta') }}
        </CustomButton>
      </template>
      <template v-else>
        <p v-if="showTeamInvite || hintText" class="text-caption text-secondary flex items-center gap-xs">
          {{ showTeamInvite ? $t('growth.firstEntrySuccess.hint') : hintText }}
        </p>
      </template>
      <div v-if="showTeamInvite && !props.isSimpleSuccess" class="flex w-full flex-col gap-lg">
        <CustomButton class="justify-center" theme="primary" :disabled="isLoading" @click="handleInvite">
          {{ $t('growth.firstEntrySuccess.inviteCta') }}
        </CustomButton>
        <CustomButton class="justify-center" theme="link" :disabled="isLoading" @click="hide">
          {{ $t('growth.firstEntrySuccess.laterCta') }}
        </CustomButton>
      </div>
      <div v-else-if="!props.isSimpleSuccess" class="flex w-full flex-col gap-md">
        <template v-if="onPrimary">
          <CustomButton class="justify-center" theme="primary" :disabled="isLoading" @click="handlePrimary">
            {{ primaryLabel ?? $t('growth.firstEntrySuccess.closeCta') }}
          </CustomButton>
          <CustomButton class="justify-center" theme="secondary" :disabled="isLoading" @click="handleSecondary">
            {{ secondaryLabel ?? $t('growth.firstEntrySuccess.closeCta') }}
          </CustomButton>
        </template>
        <CustomButton v-else class="justify-center" theme="primary" @click="hide">
          {{ $t('growth.firstEntrySuccess.closeCta') }}
        </CustomButton>
      </div>
    </div>
  </DialogBase>
</template>
